import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Button, TextField, CircularProgress, Alert, Tabs, Tab } from "@mui/material";
import { Save } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import systemSettingsService from "../../services/systemSettingsService";
import PageHeader from "../../components/common/PageHeader";

const SAMPLE_DATA = {
    deliveryDate: "7-25-2026",
    receiptNumber: "WP-2026-0024",
    customerName: "Samama Inam",
    deliveredBy: "Waypoint Imports",
    customerPhone: "0340048702",
    deliveryAddress: "20 Windward Dr, Savannah, Grand Cayman",
    packageRows: `<div style="display:flex;justify-content:space-between;padding:12px"><div><div style="font-weight:600">WP-2026-0024</div><div style="font-size:11px;color:#666">Test item</div></div></div>`,
    packageCount: 1,
    conditionStatus: "☑ Received in good condition",
    receivedByName: "Samama Inam",
    signatureBlock: `<div style="height:80px;display:flex;align-items:center;color:#999;font-size:12px">[signature image]</div>`,
    deliveryPhotoBlock: "",
    logoUrl: "/logos/waypoint-logo.png",
};

const renderPreview = (html, data) =>
    (html || "").replace(/\{\{\s*([\w]+)\s*\}\}/g, (m, key) =>
        Object.prototype.hasOwnProperty.call(data, key) ? data[key] : m,
    );

const TEMPLATE_TABS = [
    {
        label: "Printed Receipt (Admin)",
        field: "deliveryReceiptTemplateHtml",
        fetchFn: "getDeliveryReceiptTemplate",
        description: "Shown to admins after a delivery is completed — includes the signature and photo.",
    },
    {
        label: "Rider Confirmation Screen",
        field: "deliveryReceiptRiderTemplateHtml",
        fetchFn: "getDeliveryReceiptRiderTemplate",
        description: "Shown to riders while capturing a delivery — header and package details only; the confirmation checkboxes, name field, and signature pad below it are always interactive and not part of this HTML.",
    },
];

const AdminDeliveryReceiptTemplate = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [tab, setTab] = useState(0);
    const [html, setHtml] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const activeTab = TEMPLATE_TABS[tab];

    const load = (tabIndex) => {
        setLoading(true);
        const t = TEMPLATE_TABS[tabIndex];
        systemSettingsService[t.fetchFn]()
            .then(({ data }) => setHtml(data.data.templateHtml || ""))
            .catch(() => enqueueSnackbar("Failed to load template", { variant: "error" }))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load(tab);
    }, [tab]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await systemSettingsService.update({ [activeTab.field]: html });
            enqueueSnackbar("Template saved", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save template", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Delivery Receipt Templates"
                subtitle="Customize the HTML used for the printed receipt and the rider's confirmation screen"
            />

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ borderBottom: "1px solid", borderColor: "divider" }}
            >
                {TEMPLATE_TABS.map((t) => (
                    <Tab key={t.field} label={t.label} sx={{ textTransform: "none", fontWeight: 600 }} />
                ))}
            </Tabs>

            <Alert severity="info" sx={{ borderRadius: 2 }}>
                {activeTab.description}
            </Alert>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Available variables: {"{{deliveryDate}}"}, {"{{receiptNumber}}"}, {"{{customerName}}"}, {"{{deliveredBy}}"},
                        {" {{customerPhone}}"}, {"{{deliveryAddress}}"}, {"{{packageRows}}"}, {"{{packageCount}}"}
                        {tab === 0 && (
                            <>
                                , {"{{conditionStatus}}"}, {"{{receivedByName}}"}, {"{{signatureBlock}}"}, {"{{deliveryPhotoBlock}}"}
                            </>
                        )}
                        , {"{{logoUrl}}"}
                    </Alert>

                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
                        <Card>
                            <CardContent>
                                <TextField
                                    label="HTML Template"
                                    value={html}
                                    onChange={(e) => setHtml(e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={24}
                                    sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={handleSave}
                                    disabled={saving}
                                    sx={{ mt: 2 }}
                                >
                                    {saving ? <CircularProgress size={18} color="inherit" /> : "Save Template"}
                                </Button>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                                    Preview (sample data)
                                </Typography>
                                {/* Always white/dark-text, independent of app theme — this
                                    previews what actually PRINTS (always white paper), and
                                    the injected HTML assumes a white backdrop of its own. */}
                                <Box
                                    sx={{
                                        border: "1px solid #ddd",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        bgcolor: "#fff",
                                        color: "#0E1420",
                                    }}
                                    dangerouslySetInnerHTML={{ __html: renderPreview(html, SAMPLE_DATA) }}
                                />
                                {tab === 1 && (
                                    <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                                        Note: on the actual rider screen, the confirmation checkboxes, recipient name field,
                                        photo upload, and signature pad appear directly below this — they're interactive
                                        controls, not part of this HTML.
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AdminDeliveryReceiptTemplate;